/**
 * O que cada colaborador está fazendo agora, para o mapa desenhar a pessoa
 * no posto com o anel de situação e a frase ao lado ("TF-12 · Cortar tampo",
 * "529 · COZINHA", "Livre").
 *
 * Tudo é derivado — nada aqui é gravado. A pessoa está:
 * - TRABALHANDO: tem tarefa em andamento, ou um ambiente EM PRODUÇÃO num
 *   posto dela (ou em que a planilha a aponta como marceneiro responsável);
 * - PAUSADA: nada em andamento, mas uma tarefa pausada ou um ambiente
 *   pausado nos mesmos critérios;
 * - LIVRE: o resto (pode ter tarefa aguardando, que aparece na frase).
 *
 * Colaborador não tem login: quem aponta é o gestor, em nome dele.
 */
import { SITUACAO } from "./setores";
import { STATUS_AMBIENTE, STATUS_TAREFA, tarefaAberta } from "./dados/vocabulario";
import { plural } from "../../utils/dinheiro";

export const SITUACAO_PESSOA = Object.freeze({
  TRABALHANDO: "trabalhando",
  PAUSADA: "pausada",
  LIVRE: "livre",
});

export const ROTULO_SITUACAO_PESSOA = Object.freeze({
  trabalhando: "Trabalhando",
  pausada: "Em pausa",
  livre: "Livre",
});

// Cor só comunica estado: trabalhando = info (o mesmo de "em produção").
export const TOM_SITUACAO_PESSOA = Object.freeze({
  trabalhando: "info",
  pausada: "warning",
  livre: "neutral",
});

/** "Carlos Almeida" → "CA"; "Naldo" → "NA"; vazio → "?". */
export function iniciais(nome) {
  const partes = String(nome || "").trim().split(/\s+/).filter(Boolean);
  if (partes.length === 0) return "?";
  if (partes.length === 1) return partes[0].slice(0, 2).toLocaleUpperCase("pt-BR");
  return (partes[0][0] + partes[partes.length - 1][0]).toLocaleUpperCase("pt-BR");
}

/** Primeiro nome, para caber ao lado do avatar. */
export function primeiroNome(nome) {
  return String(nome || "").trim().split(/\s+/)[0] || "";
}

// Mais recente primeiro: a tarefa mexida por último é a que a pessoa está fazendo.
function maisRecentePrimeiro(a, b) {
  return String(b.atualizado_em || "").localeCompare(String(a.atualizado_em || ""));
}

function postosPorColaborador(postos) {
  const mapa = new Map();
  (postos || []).forEach((posto) => {
    if (posto.ativo === false) return;
    (posto.colaborador_ids || []).forEach((id) => {
      if (!mapa.has(id)) mapa.set(id, []);
      mapa.get(id).push(posto.id);
    });
  });
  return mapa;
}

function ambientesDaPessoa(colaboradorId, postoIds, demandasNaFabrica) {
  const noPosto = new Set(postoIds);
  return demandasNaFabrica.filter(
    (d) => (d.postoId && noPosto.has(d.postoId)) || d.colaboradorPlanilhaId === colaboradorId
  );
}

function textoDaTarefa(tarefa) {
  return [tarefa.codigo, tarefa.titulo].filter(Boolean).join(" · ");
}

function atividadeDeTarefa(tarefa) {
  return { tipo: "tarefa", texto: textoDaTarefa(tarefa), tarefa, demanda: null };
}

function atividadeDeAmbiente(demanda) {
  return { tipo: "ambiente", texto: demanda.rotulo || demanda.ambiente || "Ambiente", tarefa: null, demanda };
}

function comPrefixo(prefixo, atividade) {
  return { ...atividade, texto: `${prefixo} · ${atividade.texto}` };
}

function descreverSituacao(tarefas, ambientes) {
  const emAndamento = tarefas.find((t) => t.status === STATUS_TAREFA.EM_ANDAMENTO);
  if (emAndamento) return { situacao: SITUACAO_PESSOA.TRABALHANDO, atividade: atividadeDeTarefa(emAndamento) };
  const emProducao = ambientes.find((d) => d.statusApp === STATUS_AMBIENTE.EM_PRODUCAO);
  if (emProducao) return { situacao: SITUACAO_PESSOA.TRABALHANDO, atividade: atividadeDeAmbiente(emProducao) };

  const tarefaPausada = tarefas.find((t) => t.status === STATUS_TAREFA.PAUSADA);
  if (tarefaPausada) {
    return { situacao: SITUACAO_PESSOA.PAUSADA, atividade: comPrefixo("Pausado", atividadeDeTarefa(tarefaPausada)) };
  }
  const ambientePausado = ambientes.find((d) => d.statusApp === STATUS_AMBIENTE.PAUSADO);
  if (ambientePausado) {
    return { situacao: SITUACAO_PESSOA.PAUSADA, atividade: comPrefixo("Pausado", atividadeDeAmbiente(ambientePausado)) };
  }

  const aguardando = tarefas.filter((t) => t.status === STATUS_TAREFA.AGUARDANDO).length;
  const texto = aguardando ? `Livre · ${plural(aguardando, "tarefa na fila", "tarefas na fila")}` : "Livre";
  return { situacao: SITUACAO_PESSOA.LIVRE, atividade: { tipo: null, texto, tarefa: null, demanda: null } };
}

/**
 * Map<colaboradorId, {colaborador, situacao, atividade: {tipo, texto,
 * tarefa, demanda}, tarefasAbertas, ambientes, postoIds}> para os
 * colaboradores ATIVOS. `combinadas` = saída de combinarDemandas; só as que
 * estão num setor da fábrica contam.
 */
export function situacaoDasPessoas(estado, combinadas) {
  const naFabrica = (combinadas || []).filter((d) => d.situacao === SITUACAO.SETOR);
  const postosDe = postosPorColaborador(estado?.postos);
  const tarefasAbertas = (estado?.tarefas || []).filter(tarefaAberta).sort(maisRecentePrimeiro);
  const mapa = new Map();
  (estado?.colaboradores || []).forEach((colaborador) => {
    if (colaborador.ativo === false) return;
    const postoIds = postosDe.get(colaborador.id) || [];
    const tarefas = tarefasAbertas.filter((t) => t.colaborador_id === colaborador.id);
    const ambientes = ambientesDaPessoa(colaborador.id, postoIds, naFabrica);
    mapa.set(colaborador.id, {
      colaborador,
      ...descreverSituacao(tarefas, ambientes),
      tarefasAbertas: tarefas,
      ambientes,
      postoIds,
    });
  });
  return mapa;
}

/** {trabalhando, pausadas, livres} de uma lista de situações. */
export function contarSituacoes(situacoes) {
  const contagem = { trabalhando: 0, pausadas: 0, livres: 0 };
  (situacoes || []).forEach((s) => {
    if (s.situacao === SITUACAO_PESSOA.TRABALHANDO) contagem.trabalhando += 1;
    else if (s.situacao === SITUACAO_PESSOA.PAUSADA) contagem.pausadas += 1;
    else contagem.livres += 1;
  });
  return contagem;
}
